/**
 * The five drink profiles of KellerLog (docs/17-getraenke.md, Fünf Profile): Wein, Spirituosen,
 * Bier, Alkoholfrei, Weitere Getränke. Each is the base of every drink (drinks.ts) plus its own
 * fields, kinds, sizes, packs and reasons. Kinds are unique over all profiles, so a kind alone
 * says which profile a drink belongs to („Gin“ → Spirituosen).
 */
import { drinkFields, profileOf, sizeNote, sizeOf } from "./drinks.ts";
import type { DrinkLabels } from "./drinks.ts";
import { WINE_PROFILE, WINE_REASONS, wineName } from "./profile-wine.ts";
import type { NameOptions } from "./profile-wine.ts";
import { containerFor, profileName, profileReasons } from "./profile.ts";
import type { Profile, ProfileField, ProfileReason, WorkspaceKind } from "./profile.ts";
import { CORE_REASONS } from "./reasons.ts";
import type { Direction, Reason } from "./reasons.ts";
import { litres } from "./measures.ts";
import { PANTRY_PROFILES } from "./pantry.ts";

const COMMON: Pick<DrinkLabels, "glassHint" | "listNoHint" | "salePrice"> = {
  salePrice: "Verkaufspreis je Gebinde",
  glassHint: "wenn offen ausgeschenkt, z. B. „0,3 4,50; 0,5 6,50“ oder „2 cl 4,50; 4 cl 8,00“",
  listNoHint: "Nummer auf der Getränkekarte",
};

const deposit: ProfileField = { key: "deposit", label: "Pfand je Gebinde", type: "money", level: "article", unit: "€", group: "more", hint: "z. B. 0,08 für die Mehrwegflasche, 0,25 für die Dose" };

/** Reasons of the wine without what belongs to wine only (Korkfehler), plus „Abgelaufen“ where goods expire. */
function reasons(expires: boolean, without: string[] = []): ProfileReason[] {
  const list = WINE_REASONS.filter((r) => !without.includes(r.id));
  return expires ? [...list, { id: "spoiled", label: "Abgelaufen", class: "knownLoss", for: "both" }] : list;
}

const vocabulary = (article: string, articles: string): Profile["vocabulary"] => ({ ...WINE_PROFILE.vocabulary, article, articles, search: `${article} suchen` });

export const SPIRIT_KINDS = ["Gin", "Rum", "Whisky", "Wodka", "Brand und Obstbrand", "Grappa", "Likör", "Bitter und Aperitif", "Sonstige Spirituose"] as const;

export const SPIRITS_PROFILE: Profile = {
  id: "spirituosen",
  app: "KellerLog",
  vocabulary: vocabulary("Spirituose", "Spirituosen"),
  fields: drinkFields(
    { ...COMMON, producer: "Hersteller oder Marke", producerHint: "„Ramazzotti“, „Monkey 47“", name: "Name", nameHint: "„Amaro“, „Dry Gin“; leer, wenn die Marke alles sagt", kind: "Art", kinds: SPIRIT_KINDS, size: "Flaschengröße", sizes: [20, 40, 50, 100, 200, 350, 500, 700, 1000, 1500, 3000], containers: ["Flasche"], salePrice: "Verkaufspreis je Flasche" },
    { identity: [{ key: "age", label: "Alter oder Jahrgang", type: "text", level: "article", group: "main", hint: "„12 Jahre“, „2015“" }] },
  ).map((f) => (f.key === "wine" ? { ...f, required: false } : f)),
  groups: { main: "Spirituose", origin: "Herkunft", gastro: "Gastronomie", more: "Mehr" },
  nameFields: ["producer", "wine", "age"],
  categoryField: "color",
  packs: [{ name: "Karton à 6", plural: "Kartons à 6", factor: 6, containers: ["Flasche"] }],
  measures: { glass: { 20: "2 cl", 40: "4 cl" } },
  usualSize: 700,
  reasons: reasons(false, ["corked"]),
  tracking: "quantity",
  countMode: "article",
  starter: WINE_PROFILE.starter,
};

export const BEER_KINDS = [
  "Pils", "Helles", "Export", "Märzen und Festbier", "Weizen", "Kölsch", "Alt", "Dunkel", "Bock", "Keller- und Zwickelbier", "Pale Ale und IPA", "Stout und Porter", "Radler und Biermischgetränk", "Sonstiges Bier",
] as const;

export const BEER_PROFILE: Profile = {
  id: "bier",
  app: "KellerLog",
  vocabulary: vocabulary("Bier", "Biere"),
  fields: drinkFields(
    { ...COMMON, producer: "Brauerei", name: "Name", nameHint: "„Pilsener“, „Hefeweizen hell“", kind: "Stil", kinds: BEER_KINDS, size: "Größe", sizes: [250, 330, 500, 750, 1000, 2000, 5000, 20000, 30000, 50000], containers: ["Flasche", "Dose", "Fass"], containerHint: "leer = Flasche, ab 10 l Fass" },
    { more: [deposit] },
  ),
  groups: { main: "Bier", origin: "Herkunft", gastro: "Gastronomie", more: "Mehr" },
  nameFields: ["producer", "wine"],
  categoryField: "color",
  packs: [
    { name: "Kasten à 20", plural: "Kästen à 20", factor: 20, sizes: [500], containers: ["Flasche"] },
    { name: "Kasten à 24", plural: "Kästen à 24", factor: 24, sizes: [330], containers: ["Flasche"] },
    { name: "Tray à 24", plural: "Trays à 24", factor: 24, sizes: [330, 500], containers: ["Dose"] },
  ],
  bulk: { from: 10000, container: "Fass", reasons: { without: ["sale"], labels: { pour: "Ausschank (Fass angeschlossen)" } } },
  measures: { glass: { 500: "Halbe", 1000: "Maß" }, kinds: { Kölsch: { glass: { 200: "Stange" } } } },
  defaults: { allergens: ["gluten"] },
  expires: true,
  reasons: reasons(true, ["corked"]),
  tracking: "lot",
  countMode: "article",
  starter: WINE_PROFILE.starter,
};

export const SOFT_KINDS = [
  "Wasser", "Limonade und Cola", "Saft und Nektar", "Schorle", "Eistee", "Energy", "Tonic und Mixer", "Sirup", "Kombucha und Fermentiertes", "Milch- und Kefirgetränke", "Sonstiges Alkoholfreies",
] as const;

export const SOFT_PROFILE: Profile = {
  id: "alkoholfrei",
  app: "KellerLog",
  vocabulary: vocabulary("Getränk", "Alkoholfreie Getränke"),
  fields: drinkFields(
    { ...COMMON, producer: "Hersteller oder Marke", name: "Name", nameHint: "„Classic“, „Apfelsaft naturtrüb“", kind: "Art", kinds: SOFT_KINDS, size: "Größe", sizes: [200, 250, 330, 500, 700, 750, 1000, 1500, 5000], containers: ["Flasche", "Dose", "Bag-in-Box", "Karton"] },
    {
      identity: [{ key: "sparkling", label: "Kohlensäure", type: "choice", level: "family", options: ["still", "medium", "spritzig"], group: "main", hint: "vor allem bei Wasser" }],
      more: [deposit],
    },
  ),
  groups: { main: "Getränk", origin: "Herkunft", gastro: "Gastronomie", more: "Mehr" },
  nameFields: ["producer", "wine", "sparkling"],
  categoryField: "color",
  packs: [
    { name: "Kasten à 6", plural: "Kästen à 6", factor: 6, sizes: [1500], containers: ["Flasche"] },
    { name: "Kasten à 12", plural: "Kästen à 12", factor: 12, sizes: [700, 750, 1000], containers: ["Flasche"] },
    { name: "Kasten à 24", plural: "Kästen à 24", factor: 24, sizes: [200, 250, 330, 500], containers: ["Flasche"] },
  ],
  expires: true,
  reasons: reasons(true, ["corked"]),
  tracking: "lot",
  countMode: "article",
  starter: WINE_PROFILE.starter,
};

export const OTHER_KINDS = ["Cidre und Cider", "Obst- und Fruchtwein", "Met", "Sake", "Hard Seltzer", "Fertige Mischgetränke", "Sonstiges"] as const;

export const OTHER_PROFILE: Profile = {
  id: "weitere",
  app: "KellerLog",
  vocabulary: vocabulary("Getränk", "Weitere Getränke"),
  fields: drinkFields(
    { ...COMMON, producer: "Hersteller", name: "Name", kind: "Art", kinds: OTHER_KINDS, size: "Größe", sizes: [200, 250, 275, 330, 375, 500, 720, 750, 1000, 1500], containers: ["Flasche", "Dose"] },
    { moreFirst: [{ key: "taste", label: "Geschmack", type: "choice", level: "family", options: ["trocken", "halbtrocken", "süß"], group: "more" }], more: [deposit] },
  ),
  groups: { main: "Getränk", origin: "Herkunft", gastro: "Gastronomie", more: "Mehr" },
  nameFields: ["producer", "wine"],
  categoryField: "color",
  packs: [
    { name: "Karton à 6", plural: "Kartons à 6", factor: 6, sizes: [375, 500, 720, 750, 1000, 1500], containers: ["Flasche"] },
    { name: "Tray à 24", plural: "Trays à 24", factor: 24, sizes: [200, 250, 275, 330] },
  ],
  usualSize: 750,
  defaults: { allergens: ["sulphites"] },
  expires: true,
  reasons: reasons(true, ["corked"]),
  tracking: "lot",
  countMode: "article",
  starter: WINE_PROFILE.starter,
};

/** All drink profiles, in the order of the switcher and the cellar list. */
export const PROFILES: readonly Profile[] = [WINE_PROFILE, SPIRITS_PROFILE, BEER_PROFILE, SOFT_PROFILE, OTHER_PROFILE];

/** Short names for the switcher: „Wein | Spirituosen | Bier | Alkoholfrei | Weitere“. */
export const PROFILE_TABS: Readonly<Record<string, string>> = { wein: "Wein", spirituosen: "Spirituosen", bier: "Bier", alkoholfrei: "Alkoholfrei", weitere: "Weitere" };

/** Headings of a profile's part (cellar list, sheets of the table). */
export const PROFILE_TITLES: Readonly<Record<string, string>> = { wein: "Weine", spirituosen: "Spirituosen", bier: "Biere", alkoholfrei: "Alkoholfreie Getränke", weitere: "Weitere Getränke" };

/** Every profile of the platform: the drinks of KellerLog, then the pantry of VorratsBuch. */
export const ALL_PROFILES: readonly Profile[] = [...PROFILES, ...PANTRY_PROFILES];

export const profileById = (id: string | undefined): Profile => ALL_PROFILES.find((p) => p.id === id) ?? WINE_PROFILE;

/** The profile of an article's fields. */
export const profileFor = (f: Record<string, unknown>) => profileById(profileOf(f));

/** A drink in its profile's large container (a keg): poured by the glass, never sold whole. */
export function isBulk(f: Record<string, unknown>): boolean {
  const profile = profileFor(f);
  return profile.bulk !== undefined && containerFor(profile, sizeOf(f), f.container) === profile.bulk.container;
}

const kindProfiles = new Map<string, Profile>();
for (const p of PROFILES) for (const k of p.fields.find((f) => f.key === p.categoryField)?.options ?? []) kindProfiles.set(k, p);

/** The profile a kind belongs to („Gin“ → Spirituosen, „Rot“ → Wein); undefined for an unknown one. */
export const kindProfile = (kind: string): Profile | undefined => kindProfiles.get(kind);

/** All kinds of all profiles, in profile order. */
export const ALL_KINDS: readonly string[] = [...kindProfiles.keys()];

/**
 * The name a drink is shown and searched by: a wine „Erzeuger Name Jahrgang“, others by their
 * profile's name fields; the size where it is not the profile's usual one, the container if it is
 * not a bottle: „Jever Pils (0,33 l)“, „Jever Pils (0,5 l Dose)“.
 */
export function drinkName(f: Record<string, unknown>, options: NameOptions = {}): string {
  const profile = profileFor(f);
  if (profile.id === "wein") return wineName(f, options);
  const parts = [profileName(profile, f)];
  const ml = sizeOf(f);
  const container = typeof f.container === "string" && f.container !== "" && f.container !== "Flasche" ? f.container : "";
  if (container && ml !== null) parts.push(`(${litres(ml)} ${container})`);
  else if (profile.usualSize === undefined && ml !== null) parts.push(`(${litres(ml)})`);
  else if (sizeNote(ml, profile.usualSize)) parts.push(sizeNote(ml, profile.usualSize));
  return parts.filter(Boolean).join(" ");
}

/**
 * The reasons offered for an article, default first: its profile's, and for a large container
 * (a keg) without what does not fit it and with its own labels (A3: „Ausschank (Fass angeschlossen)“).
 */
export function articleReasons(f: Record<string, unknown>, direction: Direction, kind: WorkspaceKind = "business"): ProfileReason[] {
  const profile = profileFor(f);
  const list = profileReasons(profile, direction, kind);
  const bulk = profile.bulk?.reasons;
  if (!bulk || !isBulk(f)) return list;
  return list.filter((r) => !bulk.without.includes(r.id)).map((r) => (bulk.labels[r.id] ? { ...r, label: bulk.labels[r.id]! } : r));
}

/** Label and class of any reason id: a profile's first, then the core's (old bookings). */
export function reasonInfo(id: string): Reason | undefined {
  for (const p of ALL_PROFILES) {
    const r = p.reasons.find((x) => x.id === id);
    if (r) return r;
  }
  return CORE_REASONS.find((r) => r.id === id);
}

/** A reason's label; with the article's fields as its profile and container call it (a keg: „Ausschank (Fass angeschlossen)“). */
export const reasonLabel = (id: string, f?: Record<string, unknown>) => (f && isBulk(f) ? profileFor(f).bulk?.reasons?.labels[id] : undefined) ?? reasonInfo(id)?.label ?? id;
export const isKnownLoss = (id: string) => reasonInfo(id)?.class === "knownLoss";
